import React, { useEffect, useReducer, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import PropTypes from 'prop-types';
import MessageBox from '../../components/MessageBox';
import SpinnerOverlay from '../../components/SpinnerOverlay/SpinnerOverlay';
import BillService from '../../services/bill.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import { Button, FormField, controlClass } from '../../components/EntityList';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, bill: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

function ConfirmPayment({ id: propId, onClose, onSaved }) {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();

	const id = propId || params?.id;
	const [{ loading, error, bill }, dispatch] = useReducer(reducer, {
		bill: {},
		loading: true,
		error: '',
	});

	const [customId, setCustomId] = useState();
	const [comprobante, setComprobante] = useState('');
	const [uploading, setUploading] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const result = await BillService.getDetails(id);
				dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
				setCustomId(result.data.customId);
			} catch (error) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			}
		};

		fetchData();
	}, [id]);

	const uploadComprobante = async () => {
		if (!comprobante || comprobante.length === 0) {
			return null;
		}
		setUploading(true);
		try {
			const file = comprobante[0];
			const formData = new FormData();
			const r = Math.random().toString(36).substring(7);
			formData.append('file', file, (file.name || '').replaceAll(' ', '') + r);
			const response = await BillService.uploadComprobante(formData);
			const comprobanteId = response.data?._id;
			if (!comprobanteId) {
				throw new Error(t('msg.fileUploadFailed'));
			}
			toast.success(t('msg.fileUploadedSuccess'));
			return comprobanteId;
		} finally {
			setUploading(false);
		}
	};

	const confirmPayment = async () => {
		setSaving(true);
		try {
			const comprobanteId = await uploadComprobante();
			const payload = comprobanteId ? { comprobante: comprobanteId } : {};
			await BillService.confirmBilllPayment(bill._id || id, payload);
			toast.success(t('msg.paymentConfirmed'));
			if (onSaved) await onSaved();
			if (onClose) {
				onClose();
			} else {
				navigate(-1);
			}
		} catch (ex) {
			toast.error(getError(ex));
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (onClose) {
			onClose();
		} else {
			navigate(-1);
		}
	};

	return (
		<Modal
			size='lg'
			show={true}
			animation={false}
			onHide={handleClose}
			centered
			contentClassName='entity-list-page el-modal tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<Helmet>
				<title>{`${t('action.confirmPayment')} ${t('entity.bill')}`}</title>
			</Helmet>
			<Modal.Header closeButton={!(loading || uploading || saving)} className='tw:border-b tw:border-border tw:bg-surface-2 tw:px-5 tw:py-4'>
				<Modal.Title className='tw:text-[15px] tw:font-semibold tw:text-ink-0'>
					{t('action.confirmPayment')} {customId ? `- ${customId}` : ''}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className='tw:relative tw:bg-surface tw:px-5 tw:py-5'>
				{(loading || uploading || saving) && <SpinnerOverlay />}
				{error && <MessageBox variant='danger'>{error}</MessageBox>}
				{!error && (
					<Form onSubmit={(e) => e.preventDefault()}>
						<div className='tw:grid tw:gap-4'>
							<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface-2 tw:px-4 tw:py-3'>
								<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('entity.bill')}
								</div>
								<div className='tw:mt-1 tw:text-[18px] tw:font-semibold tw:text-ink-0'>{customId || bill.customId || id}</div>
							</div>
							<FormField id='comprobante' label={t('form.comprobante')}>
								<Form.Control
									id='comprobante'
									type='file'
									className={controlClass}
									onChange={(e) => setComprobante(e.target.files)}
									disabled={loading || uploading || saving}
								/>
							</FormField>
						</div>
					</Form>
				)}
			</Modal.Body>
			<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-5 tw:py-4'>
				<Button onClick={handleClose} disabled={saving || uploading}>{t('action.cancel')}</Button>
				<Button variant='primary' onClick={confirmPayment} disabled={loading || saving || uploading || Boolean(error)}>
					{t('action.confirm')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

ConfirmPayment.propTypes = {
	id: PropTypes.string,
	onClose: PropTypes.func,
	onSaved: PropTypes.func,
};

export default ConfirmPayment;
